const ANNOUNCEMENTS_DISMISSED_KEY = 'dismissedAnnouncements';
const ANNOUNCEMENTS_VIEWED_KEY = 'announcementsViewed';
const ANNOUNCEMENTS_SYNC_EVENT_KEY = 'announcement-sync-event';
const ANON_LAST_OPENED_TIMESTAMP_KEY = 'anon_last_opened_timestamp';
const LEGACY_ANON_LAST_OPENED_TIMESTAMP_KEY = 'marketplace_anon_last_opened_timestamp';
const ANON_SESSION_REFERENCE_KEY = 'anon_last_opened_timestamp_session_lock';
const ANON_FIRST_VISIT_SENTINEL = 'FIRST_VISIT';
const ANNOUNCEMENTS_SUMMARY_SESSION_LOCK_KEY = 'announcements_summary_session_lock';
const ANON_LAST_SUMMARY_IDS_KEY = 'anon_last_summary_announcement_ids';
const ANON_CURRENT_SESSION_NEW_IDS_KEY = 'anon_current_session_new_announcement_ids';

type AnnouncementSyncMessageType = 'VIEWED' | 'DISMISS' | 'INIT_REQUEST' | 'INIT_RESPONSE' | 'ANON_NEW_IDS';

interface AnnouncementSyncMessage {
    type: AnnouncementSyncMessageType;
    hasViewed?: boolean;
    announcementIds?: string[];
    newAnnouncementIds?: string[];
    isLoggedIn?: boolean;
    originTabId?: string;
    timestamp?: number;
}

let tabId: string | undefined;

const getAnnouncementTabId = (): string => {
    if (tabId) {
        return tabId;
    }

    tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return tabId;
};

export const getAnnouncementSyncStorageKey = (): string => {
    return ANNOUNCEMENTS_SYNC_EVENT_KEY;
};

export const publishAnnouncementSyncMessage = (message: AnnouncementSyncMessage) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        localStorage.setItem(
            ANNOUNCEMENTS_SYNC_EVENT_KEY,
            JSON.stringify({
                ...message,
                originTabId: getAnnouncementTabId(),
                timestamp: Date.now(),
            })
        );
    } catch (e) {
        // localStorage might be unavailable (e.g., private browsing)
    }
};

export const parseAnnouncementSyncMessage = (raw: string | null): AnnouncementSyncMessage | null => {
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
            return null;
        }

        if (!['VIEWED', 'DISMISS', 'INIT_REQUEST', 'INIT_RESPONSE', 'ANON_NEW_IDS'].includes(parsed.type)) {
            return null;
        }

        return parsed as AnnouncementSyncMessage;
    } catch (e) {
        return null;
    }
};

export const isAnnouncementSyncFromCurrentTab = (message: AnnouncementSyncMessage | null): boolean => {
    if (!message || !message.originTabId) {
        return false;
    }

    return message.originTabId === getAnnouncementTabId();
};

export const saveAnnouncementDismissals = (dismissedIds: string[]) => {
    try {
        sessionStorage.setItem(ANNOUNCEMENTS_DISMISSED_KEY, JSON.stringify(dismissedIds));
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const loadAnnouncementDismissals = (): string[] => {
    try {
        const stored = sessionStorage.getItem(ANNOUNCEMENTS_DISMISSED_KEY);
        if (!stored) {
            return [];
        }

        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter((item): item is string => typeof item === 'string' && item.length > 0);
    } catch (e) {
        return [];
    }
};

export const saveAnnouncementViewed = (hasViewed: boolean) => {
    try {
        sessionStorage.setItem(ANNOUNCEMENTS_VIEWED_KEY, JSON.stringify(hasViewed));
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

const parseTimestamp = (value: string | null): number | null => {
    if (!value) {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const loadAnonymousLastOpenedTimestamp = (): number | null => {
    try {
        const primaryValue = parseTimestamp(localStorage.getItem(ANON_LAST_OPENED_TIMESTAMP_KEY));
        if (primaryValue !== null) {
            return primaryValue;
        }

        const legacyValue = parseTimestamp(localStorage.getItem(LEGACY_ANON_LAST_OPENED_TIMESTAMP_KEY));
        if (legacyValue !== null) {
            // Migrate legacy value forward to the current key on first read.
            localStorage.setItem(ANON_LAST_OPENED_TIMESTAMP_KEY, String(legacyValue));
        }

        return legacyValue;
    } catch (e) {
        return null;
    }
};

export const saveAnonymousLastOpenedTimestamp = (timestamp: number) => {
    try {
        const value = String(timestamp);
        localStorage.setItem(ANON_LAST_OPENED_TIMESTAMP_KEY, value);
        localStorage.setItem(LEGACY_ANON_LAST_OPENED_TIMESTAMP_KEY, value);
    } catch (e) {
        // localStorage might be unavailable (e.g., private browsing)
    }
};

export const clearAnonymousLastOpenedTimestamp = () => {
    try {
        localStorage.removeItem(ANON_LAST_OPENED_TIMESTAMP_KEY);
        localStorage.removeItem(LEGACY_ANON_LAST_OPENED_TIMESTAMP_KEY);
    } catch (e) {
        // localStorage might be unavailable (e.g., private browsing)
    }
};

export const hasAnonymousSessionReferenceLock = (): boolean => {
    try {
        return sessionStorage.getItem(ANON_SESSION_REFERENCE_KEY) !== null;
    } catch (e) {
        return false;
    }
};

export const loadAnonymousSessionReferenceTimestamp = (): number | null => {
    try {
        const stored = sessionStorage.getItem(ANON_SESSION_REFERENCE_KEY);
        if (!stored) {
            return null;
        }

        if (stored === ANON_FIRST_VISIT_SENTINEL) {
            return null;
        }

        return parseTimestamp(stored);
    } catch (e) {
        return null;
    }
};

export const saveAnonymousSessionReferenceTimestamp = (timestamp: number | null) => {
    try {
        sessionStorage.setItem(
            ANON_SESSION_REFERENCE_KEY,
            timestamp === null ? ANON_FIRST_VISIT_SENTINEL : String(timestamp)
        );
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const clearAnonymousSessionReferenceTimestamp = () => {
    try {
        sessionStorage.removeItem(ANON_SESSION_REFERENCE_KEY);
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const hasAnnouncementsSummarySessionLock = (): boolean => {
    try {
        return sessionStorage.getItem(ANNOUNCEMENTS_SUMMARY_SESSION_LOCK_KEY) === 'true';
    } catch (e) {
        return false;
    }
};

export const lockAnnouncementsSummarySession = () => {
    try {
        sessionStorage.setItem(ANNOUNCEMENTS_SUMMARY_SESSION_LOCK_KEY, 'true');
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const wasAnnouncementsSummaryFetchedInAnotherTab = (timeoutMs = 200): Promise<boolean> => {
    if (typeof BroadcastChannel === 'undefined') {
        return Promise.resolve(false);
    }

    return new Promise(resolve => {
        const channel = new BroadcastChannel('announcements_sync');
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        let settled = false;

        const cleanup = () => {
            channel.onmessage = null;
            channel.close();
        };

        const finish = (result: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            resolve(result);
        };

        const onMessage = (event: MessageEvent) => {
            const { type, fetched, requestId: responseRequestId } = event.data || {};
            if (type === 'SUMMARY_FETCH_STATUS_RESPONSE' && responseRequestId === requestId) {
                finish(Boolean(fetched));
            }
        };

        channel.onmessage = onMessage;
        channel.postMessage({ type: 'SUMMARY_FETCH_STATUS_REQUEST', requestId });
        setTimeout(() => finish(false), timeoutMs);
    });
};

export const lockAnonymousSessionReferenceTimestamp = (): number | null => {
    const now = Date.now();

    if (hasAnonymousSessionReferenceLock()) {
        const sessionReference = loadAnonymousSessionReferenceTimestamp();
        saveAnonymousLastOpenedTimestamp(now);
        return sessionReference;
    }

    const previousOpenedAt = loadAnonymousLastOpenedTimestamp();
    saveAnonymousSessionReferenceTimestamp(previousOpenedAt);
    saveAnonymousLastOpenedTimestamp(now);
    return previousOpenedAt;
};

export const saveAnonymousLastSummaryIds = (ids: string[]): void => {
    try {
        localStorage.setItem(ANON_LAST_SUMMARY_IDS_KEY, JSON.stringify(ids));
    } catch (e) {
        // localStorage might be unavailable (e.g., private browsing)
    }
};

export const loadAnonymousLastSummaryIds = (): string[] | null => {
    try {
        const stored = localStorage.getItem(ANON_LAST_SUMMARY_IDS_KEY);
        if (!stored) {
            return null;
        }
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
            return null;
        }
        return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    } catch (e) {
        return null;
    }
};

export const broadcastAnonNewIds = (newAnnouncementIds: string[]): void => {
    publishAnnouncementSyncMessage({
        type: 'ANON_NEW_IDS',
        newAnnouncementIds,
        isLoggedIn: false,
    });

    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('announcements_sync') : null;
    if (channel) {
        channel.postMessage({
            type: 'ANON_NEW_IDS',
            newAnnouncementIds,
            isLoggedIn: false,
        });
        channel.close();
    }
};

// ─── Cross-language announcement cache ───────────────────────────────────────
// Keyed by language code so each language has its own payload, but "new/viewed/
// dismissed" state is shared (uses the same keys as before, no language suffix).
const ANNOUNCEMENTS_SUMMARY_CACHE_PREFIX = 'announcements_summary_cache_';
const ANNOUNCEMENTS_ARCHIVE_CACHE_PREFIX = 'announcements_archive_cache_';

export const saveAnnouncementsSummaryCache = (langCode: string, data: AnnouncementsSummary): void => {
    try {
        localStorage.setItem(`${ANNOUNCEMENTS_SUMMARY_CACHE_PREFIX}${langCode}`, JSON.stringify(data));
    } catch (e) {
        // localStorage might be unavailable
    }
};

export const loadAnnouncementsSummaryCache = (langCode: string): AnnouncementsSummary | null => {
    try {
        const raw = localStorage.getItem(`${ANNOUNCEMENTS_SUMMARY_CACHE_PREFIX}${langCode}`);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.Announcements)) {
            return null;
        }
        return parsed as AnnouncementsSummary;
    } catch (e) {
        return null;
    }
};

export const saveAnnouncementsArchiveCache = (langCode: string, data: Record<string, AnnouncementItem[]>): void => {
    try {
        localStorage.setItem(`${ANNOUNCEMENTS_ARCHIVE_CACHE_PREFIX}${langCode}`, JSON.stringify(data));
    } catch (e) {
        // localStorage might be unavailable
    }
};

export const loadAnnouncementsArchiveCache = (langCode: string): Record<string, AnnouncementItem[]> | null => {
    try {
        const raw = localStorage.getItem(`${ANNOUNCEMENTS_ARCHIVE_CACHE_PREFIX}${langCode}`);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return null;
        }
        return parsed as Record<string, AnnouncementItem[]>;
    } catch (e) {
        return null;
    }
};
// ─────────────────────────────────────────────────────────────────────────────

export const saveAnonymousCurrentSessionNewIds = (ids: string[]): void => {
    try {
        sessionStorage.setItem(ANON_CURRENT_SESSION_NEW_IDS_KEY, JSON.stringify(ids));
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const loadAnonymousCurrentSessionNewIds = (): string[] => {
    try {
        const stored = sessionStorage.getItem(ANON_CURRENT_SESSION_NEW_IDS_KEY);
        if (!stored) {
            return [];
        }
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    } catch (e) {
        return [];
    }
};

export const loadAnnouncementViewed = (): boolean => {
    try {
        const stored = sessionStorage.getItem(ANNOUNCEMENTS_VIEWED_KEY);
        if (!stored) {
            return false;
        }

        const parsed = JSON.parse(stored);
        return typeof parsed === 'boolean' ? parsed : false;
    } catch (e) {
        return false;
    }
};

export const loadAnnouncementViewedState = (): boolean | null => {
    try {
        const stored = sessionStorage.getItem(ANNOUNCEMENTS_VIEWED_KEY);
        if (!stored) {
            return null;
        }

        const parsed = JSON.parse(stored);
        return typeof parsed === 'boolean' ? parsed : null;
    } catch (e) {
        return null;
    }
};

const removeSessionStorageItem = (key: string) => {
    try {
        sessionStorage.removeItem(key);
    } catch (e) {
        // sessionStorage might be unavailable (e.g., private browsing)
    }
};

export const clearAnnouncementViewedState = () => removeSessionStorageItem(ANNOUNCEMENTS_VIEWED_KEY);

export const clearAnnouncementDismissals = () => removeSessionStorageItem(ANNOUNCEMENTS_DISMISSED_KEY);

export const clearAnnouncementsFields = () => {
    clearAnnouncementDismissals();
    clearAnnouncementViewedState();
};

export const handleAnnouncementSyncMessage = (
    message: {
        type?: string;
        announcementIds?: string[];
        newAnnouncementIds?: string[];
        hasViewed?: boolean;
        isLoggedIn?: boolean;
    } = {},
    dispatch: (action: { type: string; [key: string]: unknown }) => void,
    broadcastChannel: { postMessage: (msg: object) => void } | null
) => {
    const { type, announcementIds, newAnnouncementIds, hasViewed, isLoggedIn } = message;

    if (type === 'INIT_REQUEST') {
        if (broadcastChannel) {
            const currentDismissals = loadAnnouncementDismissals();
            const currentViewed = loadAnnouncementViewed();
            broadcastChannel.postMessage({
                type: 'INIT_RESPONSE',
                announcementIds: currentDismissals,
                hasViewed: currentViewed,
                newAnnouncementIds: loadAnonymousCurrentSessionNewIds()
            });
        }
        return;
    }

    if (type === 'INIT_RESPONSE') {
        if (announcementIds && announcementIds.length > 0) {
            saveAnnouncementDismissals(announcementIds);
        }
        if (hasViewed !== undefined) {
            saveAnnouncementViewed(hasViewed);
        }
        if (Array.isArray(newAnnouncementIds)) {
            saveAnonymousCurrentSessionNewIds(newAnnouncementIds);
        }
        return;
    }

    if (type === 'ANON_NEW_IDS') {
        if (Array.isArray(newAnnouncementIds)) {
            saveAnonymousCurrentSessionNewIds(newAnnouncementIds);
        }
        return;
    }

    if (type === 'DISMISS') {
        if (!isLoggedIn) {
            const currentDismissals = loadAnnouncementDismissals();
            const mergedDismissals = Array.from(new Set([...currentDismissals, ...(announcementIds || [])]));
            saveAnnouncementDismissals(mergedDismissals);
        }
        dispatch({ type: 'API_DISMISS_ANNOUNCEMENTS', announcementIds: announcementIds || [], fromBroadcast: true });
        return;
    }

    if (type === 'VIEWED') {
        if (!isLoggedIn) {
            if (hasViewed !== undefined) {
                saveAnnouncementViewed(hasViewed);
            }
        }
        // Only propagate to Redux when the sender was logged in; an anonymous open must not
        // clear ShowRedDot in a signed-in tab, which would suppress the read_at API call.
        if (hasViewed && isLoggedIn) {
            dispatch({ type: 'API_MARK_ANNOUNCEMENTS_VIEWED', fromBroadcast: true });
        }
    }
};
